import { Box, ButtonBS, Center, Text, VStack } from "@biblioteksentralen/react";
import { ResolvedLitteraturpris } from "@libry-content/common";
import { ExternalLink } from "react-feather";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { DefaultContainer } from "../../ContentContainer";
import { EventsList } from "../../events/EventsList";
import BlockContent from "../../blockContent/BlockContent";
import { Edit } from "../../editInSanity/EditInSanity";
import { CampaignViewSection } from "../CampaignViewSection";
import { useCampaignEvents } from "../useCampaignEvents";
import { LitteraturprisBanner } from "./LitteraturprisBanner";
import ItemGrid from "../../../searchAndCollection/components/works/ItemGrid";

type LitteraturprisViewProps = {
  litteraturpris: ResolvedLitteraturpris | null;
};

export const LitteraturprisView = ({ litteraturpris }: LitteraturprisViewProps) => {
  const { t, ts } = useTranslation();
  const { events, showingAllEvents, toggleAllEvents, haveAdditionalEvents } = useCampaignEvents(litteraturpris?.events);

  if (!litteraturpris?.separatePage) return null;

  if (!litteraturpris?.templateContent?.logo?.asset) {
    return (
      <DefaultContainer margin={{ base: "0", lg: "auto" }}>
        {/* Banner shows information about missing content in non-production environments */}
        <LitteraturprisBanner litteraturpris={litteraturpris} />
      </DefaultContainer>
    );
  }

  return (
    <>
      <DefaultContainer margin={{ base: "0", lg: "auto" }}>
        <LitteraturprisBanner litteraturpris={litteraturpris} />
      </DefaultContainer>
      <DefaultContainer
        margin={{ base: "0 0 3rem", sm: "0 auto 5rem", md: "0 auto 5rem" }}
        position="relative"
        padding={{ base: "0", md: "0 0.75rem" }}
      >
        <Edit
          doc={litteraturpris}
          label="Oppdater bibliotekets litteratur-side"
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          top="-2rem"
        />
        <VStack gap={{ base: "3rem", sm: "4rem" }} style={{ hyphens: "none" }} marginTop="2.5rem">
          <CampaignViewSection fontSize="lg" maxWidth="38rem" alignItems="center" gap="1rem" className="group">
            <Text display="flex" flexDirection="column">
              <Text as="span" marginBottom="1rem">
                {t(
                  "Bibliotekets litteraturpris er folkebibliotekenes pris for eksepsjonell litteratur, som deles ut årlig til en norsk bok fra de siste fem årene."
                )}
              </Text>
              <Text as="span">
                {t(
                  "Bibliotekene skal romme alt, fra skjønnlitteratur til sakprosa og lyrikk, og du kan se mange grener av litteraturen på årets kortliste."
                )}
              </Text>
            </Text>
            {!!litteraturpris.templateContent.buttonUrl && (
              <ButtonBS as="a" href={litteraturpris.templateContent.buttonUrl} variant="secondary" background="#FCB973">
                {t("Les mer og stem")}
                <ExternalLink />
              </ButtonBS>
            )}
          </CampaignViewSection>

          {!!events.length && (
            <CampaignViewSection headline={t("Kom på våre litteraturprisarrangementer")}>
              <EventsList events={events} />
              {haveAdditionalEvents && (
                <Center width="100%" zIndex="1">
                  <ButtonBS onClick={toggleAllEvents} variant="secondary">
                    {showingAllEvents ? t("Vis færre") : t("Vis alle")}
                  </ButtonBS>
                </Center>
              )}
            </CampaignViewSection>
          )}

          {!!litteraturpris.relevantPublications?.length && (
            <CampaignViewSection headline={t("Nominerte bøker")}>
              <ItemGrid items={litteraturpris.relevantPublications} />
            </CampaignViewSection>
          )}

          <CampaignViewSection>
            <Box width="100%">
              <BlockContent blocks={ts(litteraturpris.body)} />
            </Box>
          </CampaignViewSection>
        </VStack>
      </DefaultContainer>
    </>
  );
};
